import { Table as TableAntd } from 'antd';
import cn from 'classnames';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import { DraggableTableRow } from 'components/ui/Table';
import { useBasicFormStore, useEditFormStore } from 'stores/local';
import { TableRowEntityType } from 'stores/local/forms/types';

import { DictionarySettingsDrawer } from '../DictionarySettingsDrawer';
import { CreateFormulaDrawer } from '../FormulaSettingsDrawer';

import s from './EditFormTable.module.scss';
import { RequiredFieldWarning } from './components';
import { Header } from './components/Header';
import { getColumns } from './config';

const TABLE_COMPONENTS_CONFIG = {
  body: {
    row: DraggableTableRow,
  },
};

const DEFAULT_SCROLL = { x: 'max-content' };

const EditFormTable: React.FC = () => {
  const { t } = useTranslation();
  const { editFormStore } = useEditFormStore();

  const { loadingEditTable, fieldsCollection } = useBasicFormStore();

  const { handleFormFieldClick, highlightedFields } = editFormStore;

  const columns = React.useMemo(
    () => getColumns(editFormStore, t),
    [editFormStore, t]
  );

  const [height, setHeight] = React.useState<number>(0);

  const moveRow = React.useCallback(
    (dragIndex: number, hoverIndex: number) => {
      fieldsCollection.shiftElement(dragIndex, hoverIndex);
    },
    [fieldsCollection]
  );

  const onRow = React.useCallback(
    (row: TableRowEntityType, index: number) => {
      return {
        moveRow,
        index,
        onClick: () => handleFormFieldClick(row),
        rowId: row.formField.id,
        className: cn(
          s.table__row,
          highlightedFields.includes(String(row.formField.id)) &&
            s.table__row_selected
        ),
      };
    },
    [handleFormFieldClick, highlightedFields, moveRow]
  );

  /** Устанавливаем оступ снизу таблицы, чтобы при наборе формулы из дровера снизу мы могли достать до самых нижних ячеек таблицы*/
  const tableStyles = React.useMemo(() => {
    return {
      paddingBottom: height,
    };
  }, [height]);

  const getRowKey = React.useCallback((record: TableRowEntityType) => {
    return record.formField.id;
  }, []);

  return (
    <>
      <Header />
      <DndProvider backend={HTML5Backend}>
        <TableAntd
          pagination={false}
          dataSource={editFormStore.dataModel.data}
          columns={columns}
          components={TABLE_COMPONENTS_CONFIG}
          onRow={onRow}
          style={tableStyles}
          rowKey={getRowKey}
          loading={loadingEditTable}
          scroll={DEFAULT_SCROLL}
        />
      </DndProvider>
      {editFormStore.displayRequiredFieldsWarning && <RequiredFieldWarning />}
      <CreateFormulaDrawer setHeight={setHeight} />
      <DictionarySettingsDrawer />
    </>
  );
};

export default observer(EditFormTable);
